import { EuiPageTemplate } from "@inscopix/ideas-eui";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { getMarketingConsent } from "django/marketingConsentDjango";
import { AlreadyHasPersonalSpace } from "./content/AlreadyHasPersonalSpace";
import { CreatePersonalSpaceChooseRegion } from "./content/CreatePersonalSpaceChooseRegion";
import {
  FormCreatePersonalSpace,
  FormCreatePersonalSpaceState,
} from "components/PanelWelcome/FormCreatePersonalSpace";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { UserPersonalSpaceRequestStatus } from "types/constants";
import { useGetOnboardedStatus } from "hooks/useGetOnboardedStatus";
import { usePollPersonalSpace } from "hooks/usePollPersonalSpace";
import { isDefined } from "utils/isDefined";
import { createPersonalSpaceDjango } from "django/createPersonalSpaceDjango";
import { captureException } from "@sentry/react";
import { CreatePersonalSpaceError } from "./content/CreatePersonalSpaceError";
import { CreatePersonalSpaceWaiting } from "./content/CreatePersonalSpaceWaiting";
import { CreatePersonalSpaceLoading } from "./content/CreatePersonalSpaceLoading";
import { CreatePersonalSpaceSuccess } from "./content/CreatePersonalSpaceSuccess";
import { CreatePersonalSpaceRetry } from "./content/CreatePersonalSpaceRetry";

export const PageCreatePersonalSpace = () => {
  const personalSpace = useUserContext((s) => s.personalSpace);
  const hasPersonalSpace = isDefined(personalSpace);

  const {
    data: marketingConsentData,
    isInitialLoading: isMarketingConsentLoading,
  } = useQuery({
    queryKey: queryKeys.marketingConsent,
    queryFn: getMarketingConsent,
    refetchOnWindowFocus: false,
  });
  const { status, requestId, startPolling } = usePollPersonalSpace();
  // get onboarded status - on initial load could be from a previous attempt
  const { isOnboarded, isInitialLoading: isOnboardedLoading } =
    useGetOnboardedStatus();

  // track the ID of the request to create a personal space
  // so we don't confuse it with a pre-existing request
  // when choosing what to render
  const [newRequestId, setNewRequestId] = useState<number | null>(null);
  const [isRetryLoading, setIsRetryLoading] = useState(false);

  // if we see an ongoing pending request, consider it to be the new request
  // so we can render the waiting screen even if the user refreshes the page
  // while waiting for the request to complete
  useEffect(() => {
    if (
      newRequestId === null &&
      status === UserPersonalSpaceRequestStatus.PENDING
    ) {
      setNewRequestId(requestId);
    }
  }, [newRequestId, requestId, status]);

  // region selection Started
  const [choseRegion, setChoseRegion] = useState(false);
  const [syncPersonalAndHomeRegion, setSyncPersonalAndHomeRegion] =
    useState(false);

  // form status
  const [formState, setFormState] =
    useState<FormCreatePersonalSpaceState>("questions");
  const submittedForm = formState !== "questions";

  const onCompleteForm = useCallback(async () => {
    try {
      const { data } = await createPersonalSpaceDjango(
        syncPersonalAndHomeRegion,
      );
      setNewRequestId(data.id);
      startPolling();
    } catch (err) {
      captureException(err);
    }
    // start polling to check status of the personal space request
  }, [startPolling, syncPersonalAndHomeRegion]);

  const onRetryCreate = useCallback(async () => {
    setIsRetryLoading(true);
    const { data } = await createPersonalSpaceDjango(syncPersonalAndHomeRegion);
    setNewRequestId(data.id);
    startPolling();
    setIsRetryLoading(false);
  }, [startPolling, syncPersonalAndHomeRegion]);

  const content = (() => {
    // if the user already has a personal space
    // (and it's not because we just submitted the form and created one),
    // stop them here
    if (!submittedForm && newRequestId === null && hasPersonalSpace) {
      return <AlreadyHasPersonalSpace />;
    }
    // if the user submitted the form and the create request failed,
    // and it's not a previous create request (check the requestId)
    // and the user hasn't just submitted a retry
    // show an error message
    if (
      status === UserPersonalSpaceRequestStatus.FAILED &&
      requestId === newRequestId &&
      !isRetryLoading
    ) {
      return <CreatePersonalSpaceError />;
    }
    // if the create space request is pending,
    // OR if the user has submitted the form and doesn't have a personal space,
    // (and it hasn't failed, which would we caught above)
    // show a waiting screen while we're polling the request
    if (
      status === UserPersonalSpaceRequestStatus.PENDING ||
      (submittedForm && !hasPersonalSpace)
    ) {
      return <CreatePersonalSpaceWaiting />;
    }
    // if a create request is completed,
    // and the user has a personal space
    // show success!!!
    // (if this was from a previous request, we would have caught it above)
    if (
      status === UserPersonalSpaceRequestStatus.COMPLETED &&
      hasPersonalSpace
    ) {
      return <CreatePersonalSpaceSuccess personalSpace={personalSpace} />;
    }
    // if the user has not chosen a region, show them the region selection
    if (!choseRegion) {
      return (
        <CreatePersonalSpaceChooseRegion
          setChoseRegion={setChoseRegion}
          syncPersonalAndHomeRegion={syncPersonalAndHomeRegion}
          setSyncPersonalAndHomeRegion={setSyncPersonalAndHomeRegion}
        />
      );
    }
    // if we're still waiting to see if the user has onboarded already, show a loading screen
    if (isOnboardedLoading) {
      return <CreatePersonalSpaceLoading />;
    }
    // if the user has no personal space
    // and has chosen a region,
    // and already answered the questions
    // show them a button to create their personal space
    if (!submittedForm && choseRegion && isOnboarded) {
      return (
        <CreatePersonalSpaceRetry
          onRetryCreate={onRetryCreate}
          isRetryLoading={isRetryLoading}
        />
      );
    }
    // if the user has not onboarded and we're waiting to see if they have
    // agreed to marketing consent, show a loading screen
    if (!isOnboarded && isMarketingConsentLoading) {
      return <CreatePersonalSpaceLoading />;
    }
    // now that we know the user has not onboarded and we have their
    // marketing consent, show the form to create their personal space
    if (formState === "questions" || formState === "submitting") {
      return (
        <FormCreatePersonalSpace
          marketingConsentData={marketingConsentData}
          formState={formState}
          setFormState={setFormState}
          onComplete={onCompleteForm}
        />
      );
    }
    // we should never get here, but if we do, show an error message
    return <CreatePersonalSpaceError />;
  })();

  return (
    <>
      <Helmet>
        <title>Create your personal space</title>
      </Helmet>
      <EuiPageTemplate panelled={false} restrictWidth={800} paddingSize="xl">
        {content}
      </EuiPageTemplate>
    </>
  );
};
